/**
 * Supabase Auth State Management
 *
 * BETTER SOLUTION: Since we have a 4-hour JWT token, we should:
 * 1. NEVER call getSession()/getUser() - they can hang when tab is inactive
 * 2. ALWAYS use localStorage JWT token directly
 * 3. Only refresh when JWT actually expires
 *
 * This wrapper ensures ALL Supabase auth calls use localStorage instead of
 * making network requests that can hang.
 */

import { supabase } from '@/integrations/supabase/client';
import { getSupabaseSession } from '@/shared/utils/supabaseStorage';

/**
 * Initialize localStorage-based auth
 * Call this ONCE at app startup
 *
 * This replaces Supabase's getSession() and getUser() methods with
 * synchronous localStorage reads, eliminating hanging issues.
 */
export function initSupabaseAuthTimeout() {
  // Store original methods in case we need them
  const originalGetSession = supabase.auth.getSession.bind(supabase.auth);
  const originalGetUser = supabase.auth.getUser.bind(supabase.auth);

  // Replace getSession with INSTANT localStorage read
  supabase.auth.getSession = async function () {
    console.log('[AuthState] getSession() called - using localStorage (instant)');

    try {
      // Get session from localStorage - this is SYNCHRONOUS and NEVER hangs
      const localSession = getSupabaseSession();

      if (localSession) {
        // Check if token is expired
        const expiresAt = localSession.expires_at;
        if (expiresAt && Date.now() / 1000 < expiresAt) {
          console.log('[AuthState] ✅ Valid session from localStorage');
          return { data: { session: localSession }, error: null };
        } else {
          console.log('[AuthState] ⚠️ Token expired, needs refresh');
          // Token expired - try refresh (but with timeout)
          try {
            const refreshPromise = supabase.auth.refreshSession();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Refresh timeout')), 3000)
            );

            const result = await Promise.race([refreshPromise, timeoutPromise]);
            return result as any;
          } catch (error) {
            // Refresh failed or timed out - return expired session
            // API client will handle 401 and trigger proper login
            console.warn('[AuthState] Refresh failed, returning null session');
            return { data: { session: null }, error: { message: 'Session expired' } as any };
          }
        }
      }

      console.log('[AuthState] No session in localStorage');
      return { data: { session: null }, error: null };
    } catch (error: any) {
      console.error('[AuthState] getSession error:', error);
      return { data: { session: null }, error };
    }
  };

  // Replace getUser with INSTANT localStorage read
  supabase.auth.getUser = async function () {
    console.log('[AuthState] getUser() called - using localStorage (instant)');

    try {
      // Get user from localStorage session - SYNCHRONOUS, NEVER hangs
      const localSession = getSupabaseSession();

      if (localSession?.user) {
        // Check if token is still valid
        const expiresAt = localSession.expires_at;
        if (expiresAt && Date.now() / 1000 < expiresAt) {
          console.log('[AuthState] ✅ Valid user from localStorage');
          return { data: { user: localSession.user }, error: null };
        } else {
          console.log('[AuthState] ⚠️ Token expired, user invalid');
          return { data: { user: null }, error: { message: 'Token expired' } as any };
        }
      }

      console.log('[AuthState] No user in localStorage');
      return { data: { user: null }, error: null };
    } catch (error: any) {
      console.error('[AuthState] getUser error:', error);
      return { data: { user: null }, error };
    }
  };

  console.log('[AuthState] ✅ localStorage-based auth installed (NO MORE HANGING!)');
  console.log('[AuthState] getSession() and getUser() now use localStorage directly');
}
